/**
 * The one sentence a report opens with, chosen from the numbers it is about to show.
 *
 * A board of twelve counters answers every question except the first one, which is
 * always «ну и как?». Nobody wants to add "4 absences" to "12 late arrivals" and reach
 * a verdict themselves — they want to read the verdict and then check it against the
 * figures. So the screens lead with a sentence and put the board under it, not the
 * other way round.
 *
 * Everything here returns an i18n KEY and its parameters rather than text: no
 * user-facing string lives in a component, and that goes double for one assembled by
 * branching logic — a hardcoded «Пропусков: » here would be the one Russian word left
 * in the Uzbek build.
 *
 * The branches are ordered by how much the answer matters:
 *
 *   1. Is there any data at all — otherwise every counter is a zero that reads as
 *      "everybody skipped work" rather than "the terminals said nothing".
 *   2. Is there a schedule — without one the engine may not call a day late or absent,
 *      so those zeros mean "not evaluated", not "did not happen". This is the single
 *      most common reason a manager stops trusting the report, and it has to be said in
 *      the first line rather than discovered three screens down.
 *   3. Everything is clean.
 *   4. What exactly is not.
 */
import type {AttendanceSummary} from '@/entities/attendance/types';
import {
  attendanceRate,
  averageDayMinutes,
  punctualityRate,
  type RosterBreakdown,
} from './aggregate';

/** How the sentence should be painted — the same four tones every status wears. */
export type VerdictTone = 'neutral' | 'good' | 'warn' | 'bad';

export type Verdict = {
  /** Full i18n key, ready for `t()`. */
  key: string;
  params: Record<string, string | number>;
  tone: VerdictTone;
};

/**
 * What the attendance rate is a share OF: the days the schedule expected somebody in.
 *
 * The same denominator `attendanceRate` divides by, deliberately — if these two ever
 * drift apart the tile says "87%" and the hint under it says "18 of 25", and the report
 * has started contradicting its own arithmetic on one card.
 */
export function expectedDays(s: AttendanceSummary): number {
  return s.scheduled_days;
}

/**
 * Days the report actually has something to say about — used where a counter needs a
 * denominator even in a scope with no schedules, e.g. "18 / 20 days observed".
 */
export function observedDays(s: AttendanceSummary): number {
  return s.scheduled_days || s.present_days + s.absent_days + s.unscheduled_days;
}

/** Days the period says went wrong — what "needs attention" is counted from. */
export function problemDays(s: AttendanceSummary): number {
  return s.absent_days + s.late_days + s.early_leave_days + s.incomplete_days;
}

/** True when the engine had no schedule to judge against — see the module note. */
export function unjudged(s: AttendanceSummary): boolean {
  return s.scheduled_days === 0;
}

/** True when nothing at all reached the report: no days, no minutes, nobody. */
export function nothingHappened(s: AttendanceSummary): boolean {
  return (
    s.present_days === 0 &&
    s.absent_days === 0 &&
    s.unscheduled_days === 0 &&
    s.total_worked_minutes === 0
  );
}

/**
 * The headline sentence for a scope, as a key + params.
 *
 * `rate` arrives pre-formatted (`formatRate`) rather than as a fraction, so the sentence
 * and the tile above it print the same "87%" — rounding twice in two places is how the
 * two end up one percent apart.
 */
/**
 * Доля списка, начиная с которой «никто не отметился» перестаёт быть частным случаем и
 * становится тем, что определяет весь отчёт.
 *
 * Треть — не круглое число ради круглости. Пока молчит один человек из двадцати, общий
 * процент ещё описывает поведение остальных, и разговор идёт о нём. Когда молчит
 * каждый третий, процент говорит уже не о дисциплине, а о полноте данных, и вести
 * читателя надо к ней — иначе он примет решение по числу, которое отвечает не на его
 * вопрос.
 */
export const SILENT_ROSTER_SHARE = 1 / 3;

export function verdict(
  s: AttendanceSummary,
  rate: string,
  /**
   * Разбивка по людям, если отчёт вообще о нескольких. У отчёта по одному сотруднику
   * её нет, и ветка про молчащий список для него не имеет смысла: там «ни одной
   * отметки» — это и есть ответ, а не объяснение к нему.
   */
  roster?: RosterBreakdown,
): Verdict {
  if (nothingHappened(s)) {
    return {key: 'reports.verdict.empty', params: {}, tone: 'neutral'};
  }

  if (unjudged(s)) {
    return {
      key: 'reports.verdict.noSchedule',
      params: {days: s.present_days + s.unscheduled_days},
      tone: 'neutral',
    };
  }

  /*
    Молчащая часть списка идёт ПЕРЕД разбором пропусков и опозданий, потому что она
    объясняет их обоих. Отчёт на 1304 человека открывался фразой «Пропуски: 7088,
    опоздания: 1256» и процентом явки 20 — и то и другое правда, и вместе они читаются
    как «школа развалилась». На деле пять сотен человек не отметились ни разу, их
    плановые дни целиком ушли в пропуски, и первое, что надо знать читателю, — что
    считать дисциплину по этому числу нельзя, пока он не решил, почему эти люди молчат.
  */
  if (roster && roster.total > 0 && roster.silent / roster.total >= SILENT_ROSTER_SHARE) {
    return {
      key: 'reports.verdict.silentRoster',
      params: {silent: roster.silent, total: roster.total, rate},
      tone: 'warn',
    };
  }

  if (s.absent_days === 0 && s.late_days === 0) {
    return {key: 'reports.verdict.clean', params: {rate}, tone: 'good'};
  }

  return {
    key: 'reports.verdict.issues',
    params: {rate, absent: s.absent_days, late: s.late_days},
    tone: severity(s),
  };
}

/**
 * Ниже этой доли явка перестаёт быть обычным месяцем и становится происшествием.
 *
 * Девять десятых — не круглое число ради круглости: при пятидневке это примерно два
 * пропуска в месяц на человека. Один-два — это болезнь, поезд и справка; каждый десятый
 * день — это то, о чём разговаривают.
 */
export const ALARMING_ATTENDANCE_RATE = 0.9;

/**
 * Насколько всё плохо.
 *
 * Раньше правило было «есть хоть один пропуск — красный», и рассуждение под ним было
 * верным для ОДНОГО человека: пропуск — это отсутствующий человек, опоздание — горстка
 * минут. Но тот же вердикт венчает отчёт компании, где счётчик складывает всех: у
 * двенадцати человек за месяц девятнадцать пропусков — это явка 91%, то есть обычный
 * месяц, и красным он был каждый раз. Цвет, который горит всегда, не значит ничего —
 * и первым перестаёт работать там, где он действительно нужен.
 *
 * Поэтому мерой стала доля, а не количество: она одинаково честна для одного человека и
 * для двухсот, потому что делится на их же плановые дни.
 */
function severity(s: AttendanceSummary): VerdictTone {
  // Одни опоздания — это всегда «посмотреть», а не «бить тревогу».
  if (s.absent_days === 0) {
    return 'warn';
  }
  const rate = attendanceRate(s);
  return rate != null && rate >= ALARMING_ATTENDANCE_RATE ? 'warn' : 'bad';
}

/** The three figures the headline tiles show, reduced to what a tile needs. */
export type Headline = {
  attendance: number | null;
  punctuality: number | null;
  averageMinutes: number | null;
  presentDays: number;
  expected: number;
  /**
   * Числитель пунктуальности, названный вслух под плиткой.
   *
   * Это `present − late`, а НЕ `on_time_days`, — ровно то, что делит `punctualityRate`.
   * Разойдись эти двое, и плитка напечатает «29%», а подпись под ней «0 из 1772»: тот
   * самый жанр ошибки, ради которого подпись и существует.
   */
  punctualDays: number;
};

export function headline(s: AttendanceSummary): Headline {
  return {
    attendance: attendanceRate(s),
    punctuality: punctualityRate(s),
    averageMinutes: averageDayMinutes(s),
    presentDays: s.present_days,
    expected: expectedDays(s),
    punctualDays: Math.max(0, s.present_days - s.late_days),
  };
}
